import type { ComponentType } from 'react';
import type { ModuleId, ModuleMeta, ReportData } from '../data/types';

import { identityModule } from './IdentityHeader';
import { scoreModule } from './Score';
import { overviewModule } from './Overview';
import { managementModule } from './Management';
import { relatedPartiesModule } from './RelatedParties';
import { fundingModule } from './Funding';
import { mcaModule } from './MCA';
import { compliancesModule } from './Compliances';
import { legalRiskModule } from './LegalRisk';
import { alertsModule } from './Alerts';
import { auditedStatementsModule } from './AuditedStatements';
import { bankingOverviewModule } from './BankingOverview';
import { financialMetricsModule } from './FinancialMetrics';
import { mcaChargesModule } from './McaCharges';
import { directorsDeepModule } from './DirectorsDeep';
import { directorBureauModule } from './DirectorBureau';
import { riskParametersModule } from './RiskParameters';
import { gstOverviewModule } from './GstOverview';
import { gstRevenueModule } from './GstRevenue';
import { gstExpensesModule } from './GstExpenses';
import { aggBankingModule } from './AggregatedBanking';

// Sidecar registry: category label (top line on sidebar card) + Polus tabLockStatus key (if gated)
// + per-module coverage tags shown in the TOC. Kept here so individual module files stay slim.
// `defaultDisabled` modules still mount in the rail but start unselected — used during the spec-driven
// reorder so legacy modules can be re-enabled without losing their wiring.
export const MODULE_META_EXTRA: Record<ModuleId, { category: string; lockTabId?: string; coverage: string[]; defaultDisabled?: boolean }> =
    {
        identity: {
            category: 'Company',
            coverage: ['Legal name', 'PAN', 'CIN', 'Sector', 'GSTINs', 'HQ address', 'Foundation date', 'Website'],
            defaultDisabled: true,
        },
        score: {
            category: 'Company',
            coverage: ['Composite AICA score', 'Per-pillar grades', 'Weakest drivers'],
        },
        riskParameters: {
            category: 'Risk',
            coverage: ['Heat-grid by category', 'Per-metric scoring', 'Severity bands'],
        },
        overview: {
            category: 'Company',
            lockTabId: 'overview',
            coverage: [
                'Business narrative',
                'Products & customers',
                'Geographies',
                'Competitors',
                'Sector outlook',
                'News',
                'Credit ratings',
            ],
        },
        management: {
            category: 'Company',
            coverage: ['Directors with age, tenure, DIN', 'Stake & active status', 'Bureau & other boards', 'EPF headcount trend'],
        },
        directorsDeep: {
            category: 'Management',
            lockTabId: 'creditBureau',
            coverage: ['CIBIL / Equifax bureau scores', 'Legal exposure', 'Concurrent directorships', 'Phone & contact'],
            defaultDisabled: true,
        },
        directorBureau: {
            category: 'Management',
            lockTabId: 'creditBureau',
            coverage: ['Per-director bureau scores', 'CIBIL / Equifax / Experian', 'DPD accounts', 'Current overdue', 'Max DPD'],
        },
        relatedParties: {
            category: 'Structure',
            lockTabId: 'relatedParty',
            coverage: [
                'Directors',
                'Shareholders',
                'Holdings',
                'Subsidiaries',
                'Sister concerns',
                'Joint ventures',
                'Shareholding pattern',
            ],
        },
        funding: {
            category: 'Funding',
            coverage: ['Equity rounds', 'Debenture issuances', 'Instrument & amount per round', 'Total raised'],
        },
        mca: {
            category: 'Regulatory',
            coverage: ['Latest MCA filings summary', 'Filing status', 'Last refresh'],
        },
        mcaCharges: {
            category: 'Regulatory',
            lockTabId: 'creditBureau',
            coverage: ['Open charges', 'Closed charges', 'Charge holder names', 'Total open exposure', 'Creditor concentration'],
        },
        compliances: {
            category: 'Compliance',
            lockTabId: 'compliances',
            coverage: ['GSTR-1 monthly punctuality', 'GSTR-3B punctuality', 'EPF filing track record', 'Delay days per filing'],
        },
        financialMetrics: {
            category: 'Financials',
            coverage: [
                'Revenue',
                'Revenue growth %',
                'EBITA margin',
                'PAT',
                'Cash & equivalents',
                'Current assets/liabilities',
                'NWC',
                'LT/ST debt',
                'Tangible net worth',
            ],
        },
        auditedStatements: {
            category: 'Financials',
            lockTabId: 'auditedFinancials',
            coverage: [
                'Balance Sheet',
                'Profit & Loss',
                'Cashflow statement',
                'Financial Ratios',
                'Multi-year (5-year)',
                'Standalone / Consolidated',
            ],
        },
        bankingOverview: {
            category: 'Financials',
            // Polus task-tracker key for the banking & financials tab. The hook
            // reads `tabLockStatus[lockTabId]` to decide whether to mark the
            // module locked in the rail.
            lockTabId: 'bankingAndFinancials',
            coverage: ['Per-account closing balance', 'OD sanction limit', 'LTM transactions', 'Missing-day status'],
        },
        legalRisk: {
            category: 'Legal',
            lockTabId: 'legalChecks',
            coverage: ['Cases filed against', 'Cases filed by', 'Court & status per case', 'AML / PEP hits'],
        },
        alerts: {
            category: 'Risk',
            coverage: ['Severity-bucketed alerts', 'Category & reason', 'Alert date', 'Source entity'],
        },
        aggBanking: {
            category: 'Banking',
            coverage: [
                'Consolidated balances',
                'Debit & credit transactions',
                'Bounce transactions',
                'Payment method breakdown',
                'OD/CC sanction limits',
            ],
        },
        gstOverview: {
            category: 'GST',
            coverage: [
                'Total GSTINs & active count',
                'HSN & SAC active',
                'Annual turnover range',
                'State-wise GST coverage',
                'GSTIN details table',
            ],
        },
        gstExpenses: {
            category: 'GST',
            coverage: [
                'GSTR-2A/2B summary table',
                'Expense trend & growth',
                'Vendor concentration',
                'B2B vendor summary',
                'ITC utilisation',
            ],
        },
        gstRevenue: {
            category: 'GST',
            coverage: [
                'GSTR-3B summary table',
                'Revenue trend & growth',
                'Customer concentration',
                'B2B customer summary',
                'Revenue by segment & HSN',
            ],
        },
    };

export type ModuleScreenComponent = ComponentType<{ data: ReportData; index: string }>;

export type ModuleDefinition = {
    meta: ModuleMeta;
    hasData: (data: ReportData) => boolean;
    Screen: ModuleScreenComponent;
};

export const MODULES: Record<ModuleId, ModuleDefinition> = {
    identity: identityModule,
    score: scoreModule,
    riskParameters: riskParametersModule,
    overview: overviewModule,
    management: managementModule,
    directorsDeep: directorsDeepModule,
    directorBureau: directorBureauModule,
    relatedParties: relatedPartiesModule,
    funding: fundingModule,
    mca: mcaModule,
    mcaCharges: mcaChargesModule,
    compliances: compliancesModule,
    financialMetrics: financialMetricsModule,
    auditedStatements: auditedStatementsModule,
    bankingOverview: bankingOverviewModule,
    legalRisk: legalRiskModule,
    alerts: alertsModule,
    gstOverview: gstOverviewModule,
    gstRevenue: gstRevenueModule,
    gstExpenses: gstExpensesModule,
    aggBanking: aggBankingModule,
};

// Default-on stack first (in the order the V2 spec calls for), then the legacy / archived
// modules pinned to the bottom with `defaultDisabled: true` in MODULE_META_EXTRA so they
// surface in the rail unchecked. News & Sector Outlook / Key Competitors / Disclaimer slots
// are reserved in the spec but not yet built — they'll slot in around `riskParameters` and
// at the very end once their components land.
export const MODULE_ORDER: ModuleId[] = [
    // — Default visible (spec order) —
    // `riskParameters`, `identity`, and `directorsDeep` removed from the report
    // (rail + PDF). Module files, MODULES entries, and MODULE_META_EXTRA stay
    // untouched so they can be re-enabled by adding the key back here.
    'overview',
    'score',
    'management',
    'directorBureau',
    'compliances',
    'relatedParties', // ← now titled "Structure & Relations"; renders shareholding inline
    'auditedStatements',
    'financialMetrics',
    'bankingOverview',
    'funding',
    'mca',
    'mcaCharges',
    'gstOverview',
    'gstRevenue',
    'gstExpenses',
    // `aggBanking` no longer renders standalone — its content is embedded in
    // the Banking Overview section (after the accounts table).
    'legalRisk',
    'alerts',
];
